import { animate } from 'motion'
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react'

const SNAP_THRESHOLD = 0.3
const VELOCITY_THRESHOLD = 400
const RUBBER_BAND_FACTOR = 0.3
const SPRING_CONFIG = { type: 'spring', stiffness: 300, damping: 30, mass: 1 }
const PANEL_GAP = 16
const EARLY_HINT_PROGRESS = 0.55

export default function RoundCarousel({ isMobile, roundIndex, totalRounds, onGoToRound, onRoundHint, allRounds, renderRound }) {
  const trackRef = useRef(null)
  const wrapRef = useRef(null)
  const touchRef = useRef({ startX: 0, startY: 0, startTime: 0, currentX: 0, locked: null, animCtrl: null })
  const prevRoundIndexRef = useRef(roundIndex)
  const hintFiredRef = useRef(false)

  // Refs to avoid stale closures in touch handlers
  const roundIndexRef = useRef(roundIndex)
  roundIndexRef.current = roundIndex
  const totalRoundsRef = useRef(totalRounds)
  totalRoundsRef.current = totalRounds
  const onGoToRoundRef = useRef(onGoToRound)
  onGoToRoundRef.current = onGoToRound
  const onRoundHintRef = useRef(onRoundHint)
  onRoundHintRef.current = onRoundHint

  // Track swipe end position so useLayoutEffect can continue the animation smoothly
  const swipeEndPosRef = useRef(null)

  const getStride = useCallback(() => (wrapRef.current?.offsetWidth || 300) + PANEL_GAP, [])

  const setTrackX = useCallback((x) => {
    if (trackRef.current) {
      trackRef.current.style.transform = `translateX(${x}px)`
    }
  }, [])

  useLayoutEffect(() => {
    if (prevRoundIndexRef.current !== roundIndex) {
      if (touchRef.current.animCtrl) {
        touchRef.current.animCtrl.stop()
        touchRef.current.animCtrl = null
      }

      const stride = getStride()
      const goingForward = roundIndex > prevRoundIndexRef.current
      let startX

      if (swipeEndPosRef.current !== null) {
        // Continue from where the swipe left off for visual smoothness
        const swipeDx = swipeEndPosRef.current
        startX = (goingForward ? stride : -stride) + swipeDx
        swipeEndPosRef.current = null
      } else {
        // Button/dot navigation: start from full stride
        startX = goingForward ? stride : -stride
      }

      setTrackX(startX)

      const obj = { x: startX }
      touchRef.current.animCtrl = animate(obj, { x: 0 }, {
        ...SPRING_CONFIG,
        onUpdate: () => setTrackX(obj.x),
        onComplete: () => { touchRef.current.animCtrl = null },
      })
    }
    prevRoundIndexRef.current = roundIndex
  }, [roundIndex, setTrackX, getStride])

  useEffect(() => {
    if (!isMobile || !wrapRef.current) return
    const wrap = wrapRef.current

    const onTouchStart = (e) => {
      if (touchRef.current.animCtrl) {
        touchRef.current.animCtrl.stop()
        touchRef.current.animCtrl = null
      }
      hintFiredRef.current = false
      setTrackX(0)
      const t = e.touches[0]
      touchRef.current.startX = t.clientX
      touchRef.current.startY = t.clientY
      touchRef.current.currentX = 0
      touchRef.current.startTime = Date.now()
      touchRef.current.locked = null
    }

    const onTouchMove = (e) => {
      const t = e.touches[0]
      const dx = t.clientX - touchRef.current.startX
      const dy = t.clientY - touchRef.current.startY

      if (touchRef.current.locked === null) {
        if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
          touchRef.current.locked = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v'
        }
        return
      }

      if (touchRef.current.locked === 'v') return
      e.preventDefault()

      const ri = roundIndexRef.current
      const tr = totalRoundsRef.current
      const atFirst = ri <= 0
      const atLast = ri >= tr - 1
      let x = dx

      if ((atFirst && dx > 0) || (atLast && dx < 0)) {
        x = dx * RUBBER_BAND_FACTOR
      }

      const stride = getStride()
      const dragProgress = Math.abs(x) / stride
      const direction = x < 0 ? 1 : -1
      const nextIdx = ri + direction

      if (!hintFiredRef.current && dragProgress >= EARLY_HINT_PROGRESS && nextIdx >= 0 && nextIdx < tr) {
        hintFiredRef.current = true
        onRoundHintRef.current?.(nextIdx)
      } else if (hintFiredRef.current && dragProgress < EARLY_HINT_PROGRESS) {
        hintFiredRef.current = false
        onRoundHintRef.current?.(ri)
      }

      touchRef.current.currentX = x
      setTrackX(x)
    }

    const onTouchEnd = () => {
      if (touchRef.current.locked !== 'h') return

      const ri = roundIndexRef.current
      const tr = totalRoundsRef.current
      const dx = touchRef.current.currentX
      const stride = getStride()
      const elapsed = (Date.now() - touchRef.current.startTime) / 1000
      const velocity = Math.abs(dx) / Math.max(elapsed, 0.01)
      const w = wrapRef.current?.offsetWidth || 300
      const offsetRatio = Math.abs(dx) / w

      const shouldSnap = offsetRatio > SNAP_THRESHOLD || velocity > VELOCITY_THRESHOLD

      if (shouldSnap) {
        const direction = dx < 0 ? 1 : -1
        const nextIndex = ri + direction
        if (nextIndex >= 0 && nextIndex < tr) {
          hintFiredRef.current = false
          // Save swipe position so useLayoutEffect can animate from here
          swipeEndPosRef.current = dx
          // Commit immediately — not in onComplete — so rapid swipes can't lose state
          roundIndexRef.current = nextIndex
          onGoToRoundRef.current(nextIndex)
          return
        }
      }

      if (hintFiredRef.current) {
        hintFiredRef.current = false
        onRoundHintRef.current?.(ri)
      }

      const obj = { x: dx }
      touchRef.current.animCtrl = animate(obj, { x: 0 }, {
        ...SPRING_CONFIG,
        onUpdate: () => setTrackX(obj.x),
        onComplete: () => { touchRef.current.animCtrl = null },
      })
    }

    wrap.addEventListener('touchstart', onTouchStart, { passive: true })
    wrap.addEventListener('touchmove', onTouchMove, { passive: false })
    wrap.addEventListener('touchend', onTouchEnd, { passive: true })

    return () => {
      wrap.removeEventListener('touchstart', onTouchStart)
      wrap.removeEventListener('touchmove', onTouchMove)
      wrap.removeEventListener('touchend', onTouchEnd)
    }
  }, [isMobile, setTrackX, getStride])

  if (!isMobile) {
    return null
  }

  const prevRound = roundIndex > 0 ? allRounds[roundIndex - 1] : null
  const currentRound = allRounds[roundIndex]
  const nextRound = roundIndex < totalRounds - 1 ? allRounds[roundIndex + 1] : null

  return (
    <div ref={wrapRef} style={{ overflow: 'hidden', width: '100%', touchAction: 'pan-y', overscrollBehaviorX: 'none' }}>
      <div
        ref={trackRef}
        style={{ display: 'flex', gap: `${PANEL_GAP}px`, willChange: 'transform' }}
      >
        {prevRound ? (
          <div className="flex flex-col gap-6" style={{ width: '100%', flexShrink: 0, marginLeft: `calc(-100% - ${PANEL_GAP}px)` }}>
            {renderRound(prevRound)}
          </div>
        ) : (
          <div style={{ width: '100%', flexShrink: 0, marginLeft: `calc(-100% - ${PANEL_GAP}px)` }} />
        )}
        <div className="flex flex-col gap-6" style={{ width: '100%', flexShrink: 0 }}>
          {currentRound && renderRound(currentRound)}
        </div>
        {nextRound ? (
          <div className="flex flex-col gap-6" style={{ width: '100%', flexShrink: 0 }}>
            {renderRound(nextRound)}
          </div>
        ) : (
          <div style={{ width: '100%', flexShrink: 0 }} />
        )}
      </div>
    </div>
  )
}
